import { useMemo } from 'react';
import {
  parseEntityRef,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { useComponentPage } from '../ComponentPageContext';
import { targetsOf } from '../relations';
import { specOf, type ApiSpec } from './apiSpec';

/** One API a component provides, with what its definition says. */
export type ProvidedApi = {
  ref: string;
  name: string;
  title?: string;
  /** The catalog's word for its kind: openapi, graphql, asyncapi … */
  type?: string;
  /** The catalog holds no entity under the reference the component names. */
  missing?: boolean;
  /** The definition as the catalog holds it, for the download. */
  definition?: string;
  /** Asking the catalog for the definition failed. */
  definitionFailed?: boolean;
  /** Undefined when there is no definition, or it is no OpenAPI one could read. */
  spec?: ApiSpec;
};

type State = { loading: boolean; error?: Error; apis: ProvidedApi[] };

/**
 * The APIs a component provides, each with its definition — read from the
 * page's one catalog answer, which asks for the definitions anyway to show
 * the version in the facts bar; a second request would fetch the same
 * megabytes again.
 */
export function useProvidedApis(): State {
  const { entity, catalog } = useComponentPage();
  const refs = useMemo(
    () => targetsOf(entity, RELATION_PROVIDES_API),
    [entity],
  );

  // Parsed once per answer, not on every render: a large definition takes a
  // noticeable moment to read.
  const apis = useMemo(
    () =>
      refs.map((ref): ProvidedApi => {
        const api = catalog.providedApis.find(p => p.ref === ref);
        if (!api) {
          return { ref, name: parseEntityRef(ref).name, missing: true };
        }
        return {
          ref,
          name: api.name,
          title: api.title,
          type: api.type,
          definition: api.definition,
          definitionFailed: catalog.definitions === 'failed',
          spec: specOf(api.definition),
        };
      }),
    [refs, catalog.providedApis, catalog.definitions],
  );

  return {
    // A catalog that failed outright never gets as far as the definitions.
    loading:
      catalog.loading || (!catalog.error && catalog.definitions === 'loading'),
    error: catalog.error,
    apis,
  };
}
